import { useCallback, useState } from 'react';

export function useBulkSelection() {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleItem = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback((allIds: string[]) => {
    setSelectedIds(prev => {
      const allSelected = allIds.length > 0 && allIds.every(id => prev.has(id));
      if (allSelected) {
        return new Set();
      }
      return new Set(allIds);
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  /** Start a new selection from this item alone, dropping whatever came before. */
  const selectOnly = useCallback((id: string) => {
    setSelectedIds(new Set([id]));
  }, []);

  /**
   * Drop ids that no longer exist — a deleted item leaves the selection with
   * it rather than sitting on as an id every reader has to filter out. Returns
   * the same Set when nothing is stale, so this is safe to call from an effect.
   */
  const pruneSelection = useCallback((existingIds: ReadonlySet<string>) => {
    setSelectedIds(prev => {
      const kept = Array.from(prev).filter(id => existingIds.has(id));
      return kept.length === prev.size ? prev : new Set(kept);
    });
  }, []);

  const isSelected = useCallback(
    (id: string) => selectedIds.has(id),
    [selectedIds],
  );

  const isAllSelected = useCallback(
    (allIds: string[]) =>
      allIds.length > 0 && allIds.every(id => selectedIds.has(id)),
    [selectedIds],
  );

  return {
    selectedIds,
    selectedCount: selectedIds.size,
    toggleItem,
    toggleAll,
    clearSelection,
    selectOnly,
    pruneSelection,
    isSelected,
    isAllSelected,
  };
}
